import type { FC } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Button, lightColors } from '@tryhackme/thm-ui-components';
import { ThemeProvider, useTheme } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { store } from 'src/app/store';
import { setCurrentModal } from 'src/app/reducers/modal-states.reducer';
import { ModalType } from 'src/common/enums';
import { useGetExamPurchaseDataQuery } from 'src/features/certifications/certification.slice';
import { CertificationType } from 'src/common/enums/certification';
import { Countdown } from 'src/features/campaigns/components/countdown';
import { CampaignName } from 'src/common/enums/campaigns';
import { useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { checkMatch } from 'src/features/campaigns/helpers';
import { LOGIN_REFERRER } from 'src/common/constants';
import { useInfluencerCampaign } from '../../hooks/use-influencer-campaign';
import { useInfluencerAdditionalConfigs } from '../../hooks/use-influencer-additional-configs';
import { InfluencerIcon } from '../influencer-icon/influencer-icon';
import { getPathNameWithoutQueryParams } from '../../utils/influencer.formaters';
import { StyledBar, StyledCTAButtonLinkWrapper, StyledCloseBtn, StyledCopyAndIconWrapper } from './influencer-campaign-bar.styles';
import { useShowInfluencerCampaignBar } from './hooks/use-show-influencer-campaign-bar';
interface InfluencerCampaignBarProps {
  influencerCampaign: CampaignName;
}
export const InfluencerCampaignBar: FC<InfluencerCampaignBarProps> = ({
  influencerCampaign
}) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [trackEvent] = useTrackEventMutation();
  const theme = useTheme();
  const {
    pathname
  } = useLocation();
  const navigate = useNavigate();
  const {
    data: userData
  } = useGetUserQuery();
  const isLoggedIn = userData?.data?.user;
  const {
    data: purchaseData
  } = useGetExamPurchaseDataQuery({
    certificationType: CertificationType.PT1
  }, {
    skip: !userData?.data
  });
  const {
    campaign,
    isInInCurrentInfluencerSession,
    isExtended,
    shouldIncludeDays,
    userFriendlyName,
    percentageOff,
    calculatedEndTime
  } = useInfluencerCampaign(influencerCampaign);
  const [isShowInfluencerCampaignBar, hideInfluencerCampaignBar] = useShowInfluencerCampaignBar();
  const additionalConfigs = useInfluencerAdditionalConfigs(campaign?.name);
  const {
    detailsRedirect,
    pagesToShowCTA,
    barCta,
    icon
  } = additionalConfigs ?? {};
  const shouldShowCTA = pagesToShowCTA ? checkMatch(pagesToShowCTA, pathname).isWideMatch || checkMatch(pagesToShowCTA, pathname).doesMatch : false;
  useEffect(() => {
    const show = !!isInInCurrentInfluencerSession && !!campaign;
    setShouldShow(show);
  }, [isInInCurrentInfluencerSession, campaign, purchaseData, pathname, userData?.data, pagesToShowCTA, detailsRedirect]);
  const handleWelcomeGetStarted = useCallback(() => {
    if (pathname === '/welcome') {
      const {
        dispatch
      } = store;
      dispatch(setCurrentModal(ModalType.INFLUENCER_CAMPAIGN));
    }
    // eslint-disable-next-line no-void
    void trackEvent({
      event: 'click-influencer-campaign-bar',
      properties: {
        name: campaign?.name,
        page: getPathNameWithoutQueryParams(pathname)
      }
    });
    if (detailsRedirect) {
      if (isLoggedIn) {
        navigate(detailsRedirect);
      } else {
        window.sessionStorage.setItem(LOGIN_REFERRER, detailsRedirect);
        navigate('/login');
      }
    }
  }, [detailsRedirect, isLoggedIn, navigate, pathname, trackEvent, campaign?.name]);
  if (!campaign || !isShowInfluencerCampaignBar) return null;
  if (!Object.values(CampaignName).includes(campaign.name)) {
    return null;
  }
  const startTime = campaign?.startTime ?? 0;
  const barCtaContent = barCta ? barCta(userFriendlyName, percentageOff, pathname === '/onboarding') : null;
  const isGradient = ['/onboarding', '/welcome'].includes(pathname);
  return shouldShow ? <ThemeProvider theme={{
    ...theme,
    colors: lightColors
  }}>
      <StyledBar isGradient={isGradient} role="region" aria-label="Influencer Special Offer">
        <StyledCopyAndIconWrapper>
          {icon && userFriendlyName && <InfluencerIcon icon={icon} alt={userFriendlyName} />}
          <p>{barCtaContent}</p>
        </StyledCopyAndIconWrapper>
        <StyledCTAButtonLinkWrapper>
          {shouldShowCTA && detailsRedirect ? <Button onClick={handleWelcomeGetStarted}>Get Started</Button> : null}
          {!isExtended && <Countdown dark includeDays={shouldIncludeDays} startTime={startTime} endTime={calculatedEndTime} />}
        </StyledCTAButtonLinkWrapper>

        <StyledCloseBtn onClick={hideInfluencerCampaignBar}>
          <FontAwesomeIcon icon={faXmark} />
        </StyledCloseBtn>
      </StyledBar>
    </ThemeProvider> : null;
};